import { Typography } from '@mui/material'

import BoldIcon from '@hapana/shared/web/styles/assets/svgs/bold-small.svg'
import ItalicIcon from '@hapana/shared/web/styles/assets/svgs/italic-small.svg'
import UnderlineIcon from '@hapana/shared/web/styles/assets/svgs/underline-small.svg'
import LinkIcon from '@hapana/shared/web/styles/assets/svgs/link-small.svg'
import CodeIcon from '@hapana/shared/web/styles/assets/svgs/arrow-small.svg'
import Heading1Icon from '@hapana/shared/web/styles/assets/svgs/heading-1-small.svg'
import Heading2Icon from '@hapana/shared/web/styles/assets/svgs/heading-2-small.svg'
import QuoteIcon from '@hapana/shared/web/styles/assets/svgs/quote-small.svg'
import ListUlIcon from '@hapana/shared/web/styles/assets/svgs/list-ul-small.svg'
import ListOlIcon from '@hapana/shared/web/styles/assets/svgs/list-ol-small.svg'

import TextEditorLink from './text-editor-link.component'
import { styles } from './text-editor.styles'

export const POPOVER_POSITION = {
  anchorOrigin: {
    vertical: 'center',
    horizontal: 'center',
  },
  transformOrigin: {
    vertical: 'center',
    horizontal: 'center',
  },
}

export const POPOVER_FIELDS = {
  URL: 'url',
  TEXT: 'text',
}

export const LIST_TYPES = ['numbered-list', 'bulleted-list']

export const NODE_TYPE = { MARK: 'mark', BLOCK: 'block', LINK: 'link' }
export const MARK_TYPES = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  CODE: 'code',
}
export const ELEMENT_TYPES = {
  LINK: 'link',
  PARAGRAPH: 'paragraph',
  BLOCK_QUOTE: 'block-quote',
  BULLETED_LIST: 'bulleted-list',
  HEADING_ONE: 'heading-one',
  HEADING_TWO: 'heading-two',
  LIST_ITEM: 'list-item',
  NUMBERED_LIST: 'numbered-list',
  CODE: 'code',
  DIV: 'div',
  SPAN: 'span',
  BREAK: 'break',
}

export const EDITOR_TOOLS = [
  [
    {
      format: MARK_TYPES.BOLD,
      nodeType: NODE_TYPE.MARK,
      icon: BoldIcon,
    },
    {
      format: MARK_TYPES.ITALIC,
      nodeType: NODE_TYPE.MARK,
      icon: ItalicIcon,
    },
    {
      format: MARK_TYPES.UNDERLINE,
      nodeType: NODE_TYPE.MARK,
      icon: UnderlineIcon,
    },
  ],
  [
    {
      format: 'link',
      nodeType: NODE_TYPE.LINK,
      icon: LinkIcon,
    },
    {
      format: 'code',
      nodeType: NODE_TYPE.MARK,
      icon: CodeIcon,
    },
  ],
  [
    {
      format: 'heading-one',
      nodeType: NODE_TYPE.BLOCK,
      icon: Heading1Icon,
    },
    {
      format: 'heading-two',
      nodeType: NODE_TYPE.BLOCK,
      icon: Heading2Icon,
    },
    {
      format: 'block-quote',
      nodeType: NODE_TYPE.BLOCK,
      icon: QuoteIcon,
    },
  ],
  [
    {
      format: 'bulleted-list',
      nodeType: NODE_TYPE.BLOCK,
      icon: ListUlIcon,
    },
    {
      format: 'numbered-list',
      nodeType: NODE_TYPE.BLOCK,
      icon: ListOlIcon,
    },
  ],
]

export const editorElements = ({ attributes, children, element }) => ({
  [ELEMENT_TYPES.LINK]: <TextEditorLink {...{ attributes, element }}>{children}</TextEditorLink>,
  [ELEMENT_TYPES.BLOCK_QUOTE]: <blockquote {...attributes}>{children}</blockquote>,
  [ELEMENT_TYPES.BULLETED_LIST]: <ul {...attributes}>{children}</ul>,
  [ELEMENT_TYPES.HEADING_ONE]: (
    <Typography component="span" sx={styles.editorTypography} typography="header2" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.HEADING_TWO]: (
    <Typography component="span" sx={styles.editorTypography} typography="header3" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.LIST_ITEM]: <li {...attributes}>{children}</li>,
  [ELEMENT_TYPES.NUMBERED_LIST]: <ol {...attributes}>{children}</ol>,
  [ELEMENT_TYPES.PARAGRAPH]: (
    <Typography component="span" sx={styles.editorTypography} typography="medium" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.CODE]: (
    <Typography component="span" sx={styles.editorTypography} typography="medium" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.DIV]: (
    <Typography component="div" sx={styles.editorTypography} typography="medium" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.BREAK]: (
    <Typography component="div" sx={styles.editorTypography} typography="medium" {...attributes}>
      {children}
    </Typography>
  ),
  [ELEMENT_TYPES.SPAN]: { children },
})

export const textFormatElement = {
  [MARK_TYPES.BOLD]: (elementProps) => <strong>{elementProps.children}</strong>,
  [MARK_TYPES.ITALIC]: (elementProps) => <em>{elementProps.children}</em>,
  [MARK_TYPES.CODE]: (elementProps) => <code>{elementProps.children}</code>,
  [MARK_TYPES.UNDERLINE]: (elementProps) => <u>{elementProps.children}</u>,
}

export const SHORT_CODES_LIST = [
  '<AGREEMENT_CONTENT>',
  '<AGREEMENT_ID>',
  '<AMOUNT>',
  '<AndroidAppDownload>',
  '<ASSESSMENT_NAME>',
  '<AUTO_RENEW>',
  '<AUTO_RENEW_PACKAGE>',
  '<BILLING_CYCLE>',
  '<BILLING_PERIOD>',
  '<BUSINESS_EMAIL>',
  '<BUSINESS_LINK>',
  '<BUSINESS_LOGO>',
  '<BUSINESS_NAME>',
  '<BUSINESS_PHONE>',
  '<BUSINESS_WEBSITE>',
  '<CLIENT_ADDRESS>',
  '<CLIENT_DOB>',
  '<CLIENT_EMAIL>',
  '<CLIENT_FIRST_NAME>',
  '<CLIENT_GENDER>',
  '<CLIENT_ID>',
  '<CLIENT_LAST_NAME>',
  '<CLIENT_NAME>',
  '<CLIENT_PHONE>',
  '<CORPORATE_NAME>',
  '<CREDITS>',
  '<CURRENCY_SYMBOL>',
  '<Dashboard>',
  '<DATE>',
  '<DAYS_OF_WEEK>',
  '<DISCOUNTED_AMOUNT>',
  '<EMERGENCY_CONTACT_EMAIL>',
  '<EMERGENCY_CONTACT_NAME>',
  '<EMERGENCY_CONTACT_PHONE>',
  '<END_DATE>',
  '<END_TIME>',
  '<EXP_DATE>',
  '<FEEDBACK_LINK>',
  '<FIRST_BILLING_DATE>',
  '<FIRST_CREDIT_ALLOCATION_DATE>',
  '<FIRST_SECONDARY_FEE_BILLING_DATE>',
  '<FIRST_VISIT_DATE>',
  '<FIRST_VISIT_INSTRUCTOR>',
  '<FIRST_VISIT_SESSION_NAME>',
  '<FROM_EMAIL>',
  '<FROM_NAME>',
  '<GENERIC_APP_CLIENT_NAME>',
  '<GIFT_CARD_DESIGN>',
  '<GIFT_CARD_NAME>',
  '<GIFT_CARD_NUMBER>',
  '<GIFT_CARD_RECEIPT>',
  '<GIFT_CARD_VALUE>',
  '<INITIAL>',
  '<INSTRUCTOR>',
  '<INVOICE_AMOUNT>',
  '<INVOICE_LINK>',
  '<INVOICE_NUMBER>',
  '<LINK_TO_AGREEMENT>',
  '<LOCAL_TIME>',
  '<LOCATION>',
  '<LOCATION_ADDRESS>',
  '<LOGIN_LINK>',
  '<LOGIN_URL>',
  '<MASTER_ADMIN_FIRST_NAME>',
  '<MEMBERSHIP_CANCELLATION_CHARGE>',
  '<MEMBERSHIP_LENGTH>',
  '<MEMBERSHIP_NAME>',
  '<MINIMUM_CANCELLATION_TERM>',
  '<NEW_INSTRUCTOR_NAME>',
  '<OLD_INSTRUCTOR_NAME>',
  '<PACKAGE_DESCRIPTION>',
  '<PACKAGE_NAME>',
  '<Packages>',
  '<PASSWORD>',
  '<PAYMENT_DATE>',
  '<PAYMENT_DESCRIPTION>',
  '<PAYMENT_FAILURE_REASON>',
  '<PAYMENT_FREQUENCY>',
  '<PERSONAL_MESSAGE>',
  '<RECURRING_TYPE>',
  '<REMAINING_CREDITS>',
  '<RESERVATION_DATES>',
  '<RESET_PASSWORD_LINK>',
  '<RESOURCE_NUMBER>',
  '<SALESPERSON>',
  '<SALES_TAX>',
  '<SECONDARY_FEE_AMOUNT>',
  '<SECONDARY_FEE_BILLING_CYCLE>',
  '<SESSION_MESSAGE>',
  '<SESSION_NAME>',
  '<SESSION_SERIES>',
  '<SESSION_TYPE>',
  '<SETUP_FEE>',
  '<SHARED_BUSINESS_LOCATIONS>',
  '<SIGNATURE>',
  '<SIGN_UP_LINK>',
  '<SITE_ADDRESS>',
  '<STAFF_ACTION_FIRST_NAME>',
  '<START_DATE>',
  '<START_TIME>',
  '<SUSPEND_END>',
  '<SUSPEND_START>',
  '<TO_NAME>',
  '<TODAY_DATE>',
  '<TOTAL_CART_AMOUNT>',
  '<VISIT_TYPE>',
  '<WAITLIST_LINK>',
  '<Workshops>',
  '<ZOOM_JOIN_URL>',
]

export const SHORT_CODES_OPTIONS = SHORT_CODES_LIST.map((shortCode) => ({
  label: shortCode,
  value: shortCode,
}))
